import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { icons } from '../../../assets/icons';
import './index.scss';
import { imageApi } from '../../../api/redux/slices/Public';
import { showToast } from '../../../components/modal';
import { ButtonComponent } from '../../../components/buttons';
import { LoaderComponent } from '../../../helpers/components/loader';
import Avatar from '../../../components/avatar';
import { 
    getPayementDetails, 
    addPayementDelivery, 
    updatePayementDelivery, 
    deletePayementDelivery 
} from '../../../api/redux/slices/Admin';
import { convertDate, roundToThreeDecimals } from '../../../helpers/modules';

const PaymentDetails = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const token = localStorage.getItem('accesstoken');

    const delivery_id = params.delivery_id || params.id;
    const page = 1;

    const [loading, setLoading] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState({ payments: [], delivery: {}, totalPaid: 0, remainingAmount: 0 });
    const [fileUploaded, setFileUploaded] = useState({});
    
    const [showAddModal, setShowAddModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        price: '',
        type: 'cash',
        message: '',
    });

    const fetchPaymentDetails = useCallback(() => {
        if (!delivery_id) {
            showToast('ID de livraison manquant', 'error');
            return;
        }
        setLoading(true);
        dispatch(getPayementDetails({ token, delivery_id }))
            .unwrap()
            .then((res) => {
                setPaymentDetails(res.data);
            })
            .catch((err) => {
                showToast(err?.message || 'Erreur lors du chargement', 'error');
            })
            .finally(() => setLoading(false));
    }, [dispatch, token, delivery_id]);

    useEffect(() => {
        fetchPaymentDetails();
    }, [fetchPaymentDetails]);

    // --------------Add payment with image--------------------
    const handleAddPayment = (imgObject = {}) => {
        setModalLoading(true);
        dispatch(addPayementDelivery({
            token,
            delivery_id,
            data: {
                type: formData.type,
                price: parseFloat(formData.price),
                message: formData.message,
                image: imgObject
            }
        }))
            .unwrap()
            .then(() => {
                showToast('Paiement ajouté avec succès', 'success');
                setShowAddModal(false);
                resetForm();
                fetchPaymentDetails();
            })
            .catch((err) => {
                showToast(err?.message || 'Erreur lors de l\'ajout', 'error');
            })
            .finally(() => setModalLoading(false));
    };

    const addPaymentWithImg = () => {
        let formDataImg = new FormData();
        formDataImg.append('image', fileUploaded?.file);

        dispatch(imageApi({ token: token, formDta: formDataImg }))
            .unwrap()
            .then((res) => {
                handleAddPayment(res);
            })
            .catch((err) => {
                showToast(err.message || err, 'error');
                setModalLoading(false);
            });
    };

    // --------------Update payment with image--------------------
    const handleUpdatePayment = (imgObject = {}) => {
        setModalLoading(true);
        dispatch(updatePayementDelivery({
            token,
            payment_id: selectedPayment._id,
            data: {
                type: formData.type,
                price: parseFloat(formData.price),
                message: formData.message,
                image: imgObject
            }
        }))
            .unwrap()
            .then(() => {
                showToast('Paiement modifié avec succès', 'success');
                setShowUpdateModal(false);
                resetForm();
                fetchPaymentDetails();
            })
            .catch((err) => {
                showToast(err?.message || 'Erreur lors de la modification', 'error');
            })
            .finally(() => setModalLoading(false));
    };

    const updatePaymentWithImg = () => {
        let formDataImg = new FormData();
        formDataImg.append('image', fileUploaded?.file);

        dispatch(imageApi({ token: token, formDta: formDataImg }))
            .unwrap()
            .then((res) => {
                handleUpdatePayment(res);
            })
            .catch((err) => {
                showToast(err.message || err, 'error');
                setModalLoading(false);
            });
    };

    // --------------Delete payment--------------------
    const handleDeletePayment = () => {
        setModalLoading(true);
        dispatch(deletePayementDelivery({ token, payment_id: selectedPayment._id }))
            .unwrap()
            .then(() => {
                showToast('Paiement supprimé avec succès', 'success');
                setShowDeleteModal(false);
                fetchPaymentDetails();
            })
            .catch((err) => {
                showToast(err?.message || 'Erreur lors de la suppression', 'error');
            })
            .finally(() => setModalLoading(false));
    };

    const resetForm = () => {
        setFormData({
            price: '',
            type: 'cash',
            message: '',
        });
        setFileUploaded({});
        setSelectedPayment(null);
    };

    const openAddModal = () => {
        resetForm();
        setShowAddModal(true);
    };

    const openUpdateModal = (payment) => {
        setSelectedPayment(payment);
        setFormData({
            price: payment.price,
            type: payment.type,
            message: payment.message || '',
        });
        if (payment.image?.secure_url) {
            setFileUploaded({ url: payment.image.secure_url });
        }
        setShowUpdateModal(true);
    };

    const openDeleteModal = (payment) => {
        setSelectedPayment(payment);
        setShowDeleteModal(true);
    };

    const handleSubmit = () => {
        const price = parseFloat(formData.price);
        const remaining = paymentDetails.remainingAmount;
        
        if (!price || price <= 0) {
            showToast('Veuillez entrer un montant valide', 'warn');
            return;
        }
        
        if (price > remaining) {
            showToast(`Le montant ne peut pas dépasser le reste à payer: ${remaining} DT`, 'warn');
            return;
        }

        if (fileUploaded?.file) {
            addPaymentWithImg();
        } else {
            handleAddPayment();
        }
    };

    const handleUpdate = () => {
        const price = parseFloat(formData.price);
        const totalPaidWithoutCurrent = paymentDetails.totalPaid - (selectedPayment?.price || 0);
        const maxAllowed = paymentDetails.delivery.total - totalPaidWithoutCurrent;
        
        if (!price || price <= 0) {
            showToast('Veuillez entrer un montant valide', 'warn');
            return;
        }
        
        if (price > maxAllowed) {
            showToast(`Le montant ne peut pas dépasser ${maxAllowed} DT`, 'warn');
            return;
        }

        if (fileUploaded?.file) {
            updatePaymentWithImg();
        } else {
            handleUpdatePayment(selectedPayment?.image || {});
        }
    };

    const getPaymentTypeLabel = (type) => {
        switch(type) {
            case 'cash': return 'Espèces';
            case 'cheque': return 'Chèque';
            case 'bankTransfer': return 'Virement bancaire';
            default: return type;
        }
    };

    const totalPaid = paymentDetails?.totalPaid || 0;
    const totalDelivery = paymentDetails?.delivery?.total || 0;
    const remainingAmount = totalDelivery - totalPaid;

    if (loading) {
        return <LoaderComponent />;
    }

    return (
        <div id="paymentDetails">
            <div className="details-payment-header">
                <Link to={page ? `/admin/deliveries/${page}` : "/admin/deliveries"}>
                    <icons.BsArrowLeft style={{ color: '#5B5B5B', marginRight: '10px' }} />
                    <h1>Gestion des paiements</h1>
                </Link>
                <button className="button-primary" onClick={openAddModal}>
                    <icons.FiPlus />
                    Ajouter un paiement
                </button>
            </div>

            {/* Delivery Info Section */}
            <div className="delivery-info">
                <div className="info-grid">
                    <div className="info-item">
                        <label>Référence livraison</label>
                        <span>{paymentDetails?.delivery?.reference || '-'}</span>
                    </div>
                    <div className="info-item">
                        <label>Date</label>
                        <span>{convertDate(paymentDetails?.delivery?.date, 'DD-MM-YYYY') || '-'}</span>
                    </div>
                    <div className="info-item">
                        <label>Client</label>
                        <span>{paymentDetails?.delivery?.client || '-'}</span>
                    </div>
                    <div className="info-item">
                        <label>Société</label>
                        <span>{paymentDetails?.delivery?.company_name || '-'}</span>
                    </div>
                    <div className="info-item">
                        <label>Total à payer</label>
                        <span>{roundToThreeDecimals(totalDelivery)} DT</span>
                    </div>
                </div>
            </div>

            {/* Totals Section */}
            <div className="payment-summary">
                <div className="summary-card">
                    <h3>Total payé</h3>
                    <span className="paid">{roundToThreeDecimals(totalPaid)} DT</span>
                </div>
                <div className="summary-card">
                    <h3>Reste à payer</h3>
                    <span className="remaining">{roundToThreeDecimals(remainingAmount)} DT</span>
                </div>
            </div>

            {/* Payments Table */}
            <div className="payments-table-container">
                <table className="payments-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Montant</th>
                            <th>Type</th>
                            <th>Message</th>
                            <th>Image</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paymentDetails?.payments?.length > 0 ? (
                            paymentDetails.payments.map((payment, index) => (
                                <tr key={payment._id || index}>
                                    <td>{convertDate(payment.createdAt, 'DD-MM-YYYY')}</td>
                                    <td>{roundToThreeDecimals(payment.price)} DT</td>
                                    <td>{getPaymentTypeLabel(payment.type)}</td>
                                    <td>{payment.message || '-'}</td>
                                    <td>
                                        {payment.image?.secure_url ? (
                                            <a href={payment.image.secure_url} target="_blank" rel="noopener noreferrer">
                                                Voir image
                                            </a>
                                        ) : '-'}
                                    </td>
                                    <td className="actions">
                                        <icons.BsPencil onClick={() => openUpdateModal(payment)} className="edit-icon" />
                                        <icons.BsTrash onClick={() => openDeleteModal(payment)} className="delete-icon" />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="empty-row">Aucun paiement enregistré</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add Payment Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Ajouter un paiement</h2>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Montant (max: {roundToThreeDecimals(remainingAmount)} DT)</label>
                                <input
                                    type="number"
                                    placeholder="Montant"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Type de paiement</label>
                                <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                    <option value="cash">Espèces</option>
                                    <option value="cheque">Chèque</option>
                                    <option value="bankTransfer">Virement bancaire</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Message (optionnel)</label>
                                <textarea
                                    placeholder="Message"
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Image (optionnel)</label>
                                <div className="field-image-upload">
                                    <Avatar
                                        fileUploaded={fileUploaded}
                                        setFileUploaded={setFileUploaded}
                                        width="100%"
                                        height="150px"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="button-nature" onClick={() => setShowAddModal(false)}>Annuler</button>
                            <ButtonComponent name="Ajouter" loading={modalLoading} type="button" className="button-primary" onClick={handleSubmit} />
                        </div>
                    </div>
                </div>
            )}

            {/* Update Payment Modal */}
            {showUpdateModal && (
                <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Modifier le paiement</h2>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Montant</label>
                                <input
                                    type="number"
                                    placeholder="Montant"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Type de paiement</label>
                                <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                    <option value="cash">Espèces</option>
                                    <option value="cheque">Chèque</option>
                                    <option value="bankTransfer">Virement bancaire</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Message</label>
                                <textarea
                                    placeholder="Message"
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Image (optionnel)</label>
                                <div className="field-image-upload">
                                    <Avatar
                                        fileUploaded={fileUploaded}
                                        setFileUploaded={setFileUploaded}
                                        width="100%"
                                        height="150px"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="button-nature" onClick={() => setShowUpdateModal(false)}>Annuler</button>
                            <ButtonComponent name="Modifier" loading={modalLoading} type="button" className="button-primary" onClick={handleUpdate} />
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Payment Modal */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Confirmer la suppression</h2>
                        <p>Êtes-vous sûr de vouloir supprimer ce paiement de {roundToThreeDecimals(selectedPayment?.price)} DT ?</p>
                        <div className="modal-footer">
                            <button className="button-nature" onClick={() => setShowDeleteModal(false)}>Annuler</button>
                            <ButtonComponent name="Supprimer" loading={modalLoading} type="button" className="button-refuser" onClick={handleDeletePayment} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentDetails;